import React from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { formatCalendarDate } from '../../utils/calendarDate';
import { track, EVENTS } from '../../services/analytics';

// Pieces of the public college pages (#2579). Every fact on a college page
// sits beside its source: the official page or file, and the date we read it.

const sourceKey = (key) => `college_${key}`.slice(0, 40);

export function SourceNote({ sources, sourceKeyName, where, className = '' }) {
    const s = sources[sourceKeyName];
    if (!s) return null;
    return (
        <p className={`text-xs text-[#6B6B6B] ${className}`}>
            Source:{' '}
            <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track(EVENTS.SOURCE_LINK_CLICKED, { source: sourceKey(sourceKeyName), surface: 'college-page' })}
                className="inline-flex items-center gap-0.5 font-medium text-[#1A4D2E] underline underline-offset-2"
            >
                {s.label}
                <ArrowTopRightOnSquareIcon className="h-3 w-3" aria-hidden="true" />
                <span className="sr-only"> (opens in a new tab)</span>
            </a>
            {where ? `, ${where}` : ''}. Read {formatCalendarDate(s.read)}.
        </p>
    );
}

export function FactTile({ label, value, detail }) {
    return (
        <div className="rounded-2xl border border-[#E0DED8] bg-white p-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#6B6B6B]">{label}</p>
            <p className="mt-1 font-serif text-2xl sm:text-3xl font-semibold text-[#1A2E1F] tabular-nums">{value}</p>
            {detail && <p className="mt-1 text-[13px] leading-snug text-[#4A4A4A]">{detail}</p>}
        </div>
    );
}

export function FactSection({ id, title, children }) {
    return (
        <section aria-labelledby={id} className="border-t border-[#E0DED8] pt-8 mt-10 first:mt-0 first:border-t-0 first:pt-0">
            <h2 id={id} className="font-serif text-2xl sm:text-3xl font-bold text-[#1A2E1F]">{title}</h2>
            <div className="mt-4 space-y-3 text-[15px] leading-relaxed text-[#4A4A4A]">{children}</div>
        </section>
    );
}

// The cycle a requirement belongs to (#2651): "For students applying in fall
// 2026, entering fall 2027". Every requirement block opens with one.
export function CycleNote({ line, testId }) {
    if (!line) return null;
    return (
        <p data-testid={testId} className="inline-block rounded-lg bg-[#EEF4EC] px-3 py-1 text-[13px] font-semibold text-[#1A4D2E]">
            {line}
        </p>
    );
}

// A table's year, said in words above it (#2651: every outcome figure names
// its year).
export function TableYear({ children }) {
    return <p className="text-[13px] font-semibold text-[#1A2E1F]">{children}</p>;
}
